import { useCallback, useMemo } from 'react';

import { useProfile } from '../contexts/ProfileContext';
import { SUBSCRIPTION_REQUIRED_EVENT, type SubscriptionRequiredEventDetail } from '../lib/api';

/**
 * Superfície que originou o paywall. Vai como prop `origin` nos eventos
 * PostHog — `self_grade` é o sinal que mede a tese do modelo novo (a
 * autocorreção gratuita converte em assinatura).
 */
export type PaywallOrigin =
  'self_grade' | 'chat' | 'dashboard' | 'competencies' | 'history_compare';

export type AccessMode = 'FULL_ACCESS' | 'FREE' | 'READ_ONLY';

export interface SubscriptionGate {
  /** Assinatura ativa (ou cancelada dentro do período pago). */
  hasFullAccess: boolean;
  /** Nunca assinou ou a assinatura expirou → copy "assine". */
  isFree: boolean;
  /** `past_due`/`unpaid` → copy "regularize o pagamento", CTA para o portal. */
  isPastDue: boolean;
  accessMode: AccessMode;
  /** Perfil ainda carregando: não decidir nada com base no gate. */
  loading: boolean;
  /**
   * Guarda de clique. Devolve `true` quando a ação pode seguir; quando não,
   * abre o paywall e devolve `false`.
   */
  requireSubscription: (origin: PaywallOrigin) => boolean;
}

/**
 * Normaliza o `accessMode` de `GET /me`. Qualquer valor desconhecido cai em
 * `FREE`: nunca conceder acesso por acidente, e "assine" é a copy correta para
 * quem não tem assinatura identificável (só `past_due`/`unpaid` merece
 * "regularize").
 */
export function normalizeAccessMode(raw: unknown): AccessMode {
  if (raw === 'FULL_ACCESS' || raw === 'READ_ONLY') return raw;
  return 'FREE';
}

/**
 * Abre o paywall de assinatura sem passar pelo backend. Usado pelo gate da UI
 * no clique de um recurso pago — o 403 do `apiClient` dispara o mesmo evento,
 * então há um único paywall montado no root (`App.tsx`).
 */
export function openSubscriptionPaywall(origin: PaywallOrigin, accessMode: AccessMode): void {
  const detail: SubscriptionRequiredEventDetail = { origin, accessMode };
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_REQUIRED_EVENT, { detail }));
}

/**
 * Gate de assinatura da UI (Fase 9). Lê `accessMode` do `ProfileContext` — o
 * mesmo campo que o backend usa em `requireSubscription` — e decide se uma
 * superfície paga (correção por IA, chat, analytics) está liberada.
 *
 * O backend continua sendo a autoridade: este hook só evita levar a pessoa até
 * um 403 cru e escolhe a copy certa para o estado dela.
 */
export function useSubscriptionGate(): SubscriptionGate {
  const { data, loading } = useProfile();
  const accessMode = normalizeAccessMode(data?.accessMode);
  const hasFullAccess = accessMode === 'FULL_ACCESS';

  const requireSubscription = useCallback(
    (origin: PaywallOrigin) => {
      // Perfil ainda carregando: liberar e deixar o backend decidir. Um
      // assinante não pode levar paywall só porque o `/me` não voltou ainda;
      // se de fato não tiver acesso, o 403 abre o mesmo paywall com o
      // `accessMode` autoritativo.
      if (loading) return true;
      if (hasFullAccess) return true;
      openSubscriptionPaywall(origin, accessMode);
      return false;
    },
    [hasFullAccess, accessMode, loading]
  );

  return useMemo(
    () => ({
      hasFullAccess,
      isFree: accessMode === 'FREE',
      isPastDue: accessMode === 'READ_ONLY',
      accessMode,
      loading,
      requireSubscription,
    }),
    [hasFullAccess, accessMode, loading, requireSubscription]
  );
}
